import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { API } from "@/lib/creatorApi";
import { fmtTime, listEnhancements } from "@/lib/enhanceApi";
import { clipFromEnhance, clipFromTake } from "@/lib/myVoicesApi";
import { safeApiError } from "@/lib/safeErrorText";

const studio = (p) => axios.get(`${API}/rec-studio${p}`, { withCredentials: true }).then((r) => r.data);

function Row({ testId, title, sub, added, onAdd }) {
  const [busy, setBusy] = useState(false);
  return (
    <li className="flex items-center gap-3 py-2.5">
      <span className="min-w-0 flex-1"><span className="block truncate text-[13px] font-medium">{title}</span><span className="block truncate text-[11px] text-white/45">{sub}</span></span>
      <button type="button" data-testid={testId} disabled={busy || added} onClick={async () => { setBusy(true); await onAdd(); setBusy(false); }}
        className="inline-flex items-center gap-1 rounded-full border border-cyan-300/30 px-3 py-1 text-[12px] text-cyan-100 hover:bg-cyan-300/10 disabled:opacity-50">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : added ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}{added ? "Added" : "Add"}
      </button>
    </li>
  );
}

function useAdder(onAdded) {
  const [added, setAdded] = useState({});
  const add = (key, fn) => async () => {
    try { await fn(); setAdded((a) => ({ ...a, [key]: true })); onAdded(); } catch (e) { toast.error("Couldn't add it", { description: safeApiError(e) }); }
  };
  return [added, add];
}

function EnhancedList({ voiceId, onAdded }) {
  const [rows, setRows] = useState(null);
  const [added, add] = useAdder(onAdded);
  useEffect(() => { listEnhancements().then((r) => setRows(r.filter((x) => x.versions?.natural))).catch(() => setRows([])); }, []);
  if (rows === null) return <Loader2 className="h-5 w-5 animate-spin text-white/40" />;
  if (!rows.length) return <p className="text-[13px] text-white/50">No enhanced vocals yet — use Enhance My Vocals first.</p>;
  return (
    <ul className="max-h-80 divide-y divide-white/[0.06] overflow-y-auto">
      {rows.map((r) => <Row key={r.id} testId={`pick-enhance-${r.id}`} title={r.title} sub={`${fmtTime(r.info?.duration)} · Natural Voice version${r.isolate ? " · music removed" : ""}`} added={added[r.id]} onAdd={add(r.id, () => clipFromEnhance(voiceId, r.id))} />)}
    </ul>
  );
}

function TakeList({ voiceId, onAdded }) {
  const [sessions, setSessions] = useState(null);
  const [sid, setSid] = useState("");
  const [tracks, setTracks] = useState([]);
  const [added, add] = useAdder(onAdded);
  useEffect(() => { studio("/sessions").then(setSessions).catch(() => setSessions([])); }, []);
  useEffect(() => { setTracks([]); if (sid) studio(`/sessions/${sid}`).then((s) => setTracks(s.tracks || [])).catch(() => setTracks([])); }, [sid]);
  if (sessions === null) return <Loader2 className="h-5 w-5 animate-spin text-white/40" />;
  if (!sessions.length) return <p className="text-[13px] text-white/50">No Recording Studio sessions yet.</p>;
  const takes = tracks.flatMap((t) => (t.takes || []).map((k) => ({ ...k, track: t.name })));
  return (
    <div>
      <select data-testid="pick-take-session" value={sid} onChange={(e) => setSid(e.target.value)} className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-[13px] text-white">
        <option value="">Choose a session…</option>
        {sessions.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
      </select>
      <ul className="mt-2 max-h-72 divide-y divide-white/[0.06] overflow-y-auto">
        {sid && !takes.length ? <li className="py-3 text-[13px] text-white/50">No takes in this session.</li> : null}
        {takes.map((k) => <Row key={k.id} testId={`pick-take-${k.id}`} title={k.name} sub={`${k.track} · ${fmtTime(k.duration)}`} added={added[k.id]} onAdd={add(k.id, () => clipFromTake(voiceId, sid, k.id))} />)}
      </ul>
    </div>
  );
}

/** Reuse vocals you already have: an Enhanced vocal (Natural version) or a Recording Studio take. */
export default function PickSourceDialog({ open, kind, onClose, voiceId, onAdded }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`pick-${kind}-dialog`} className="max-w-md border-white/10 bg-[#0b0e1c] text-white">
        <DialogHeader>
          <DialogTitle className="font-display">{kind === "enhance" ? "Add from Enhanced Vocals" : "Add from Recording Studio takes"}</DialogTitle>
          <DialogDescription className="text-white/60">Pick vocals where it's only you singing. Each one is cleaned automatically.</DialogDescription>
        </DialogHeader>
        {open && kind === "enhance" ? <EnhancedList voiceId={voiceId} onAdded={onAdded} /> : null}
        {open && kind === "take" ? <TakeList voiceId={voiceId} onAdded={onAdded} /> : null}
      </DialogContent>
    </Dialog>
  );
}
